/**
 * Nouns: the things of schedule-bit a hand can pick up, and the holes they fill.
 *
 * A hole takes a kind of noun by what its schema is (`$ref` component, REST
 * collection, the shape of a position), never by the operation's id, so an
 * operation added to the spec tomorrow takes the same drops as today's:
 * `acceptsOf` says which, and the Deck binds a held thing's nouns to the
 * holes that accept them. JSON pointers into Args are here too, the one
 * copy the Deck and the catalogue read and write arguments with.
 */

import type { Args, Hole, Json, NounKind } from '../contracts';
import { isObj, type JsonObject } from '../model/json';

// -- pointers into Args -----------------------------------------------------------------------

const unescape = (s: string) => s.replace(/~1/g, '/').replace(/~0/g, '~');
export const escapePointer = (s: string) => s.replace(/~/g, '~0').replace(/\//g, '~1');

/** A pointer's segments, unescaped: `/body/a~1b` → ['body', 'a/b']. */
export const segmentsOf = (pointer: string): string[] => (pointer === '' || pointer === '/' ? [] : pointer.split('/').slice(1).map(unescape));

/** The value at a JSON pointer into Args (`/w`, `/body/who`); undefined when absent. */
export const getAt = (args: Json, pointer: string): Json | undefined => {
  let node: Json | undefined = args;
  for (const part of segmentsOf(pointer)) {
    if (Array.isArray(node)) node = node[Number(part)];
    else if (isObj(node)) node = node[part];
    else return undefined;
  }
  return node;
};

/** What `resolvePointer` answers where nothing is: a hole left empty. */
export const MISSING: unique symbol = Symbol('missing');

/**
 * The value at a pointer, where `*` means every element of an array, and every
 * one must have it (missing when there are none): whether a required hole is
 * filled.
 */
export const resolvePointer = (root: Json | Args, pointer: string): Json | typeof MISSING => {
  const parts = segmentsOf(pointer);
  const walk = (node: Json | Args | undefined, i: number): Json | typeof MISSING => {
    if (i === parts.length) return node === undefined ? MISSING : (node as Json);
    const part = parts[i]!;
    if (part === '*') {
      if (!Array.isArray(node) || node.length === 0) return MISSING;
      for (const item of node) if (walk(item, i + 1) === MISSING) return MISSING;
      return node;
    }
    if (Array.isArray(node)) return walk(node[Number(part)], i + 1);
    if (isObj(node)) return walk(node[part], i + 1);
    return MISSING;
  };
  return walk(root, 0);
};

/** Every value a pointer reaches, `*` walking every element of an array: what a hole holds, wherever it repeats. */
export const valuesAt = (root: Json, pointer: string): Json[] => {
  let here: Json[] = [root];
  for (const part of segmentsOf(pointer)) {
    const next: Json[] = [];
    for (const v of here) {
      if (part === '*' && Array.isArray(v)) next.push(...v);
      else if (isObj(v) && v[part] !== undefined) next.push(v[part]!);
    }
    here = next;
  }
  return here;
};

/** Args with the value at `pointer` set (objects made along the way). `*` segments are the editors' business. */
export const setAt = (args: Args, pointer: string, value: Json): Args => {
  const parts = segmentsOf(pointer);
  if (parts.length === 0 || parts.includes('*')) return args;
  const put = (node: Json | undefined, i: number): Json => {
    const k = parts[i]!;
    const base: JsonObject = isObj(node) ? { ...node } : {};
    base[k] = i === parts.length - 1 ? value : put(base[k], i + 1);
    return base;
  };
  return put(args as JsonObject, 0) as Args;
};

/** Args without the value at `pointer`, and without objects it leaves empty. */
export const unsetAt = (args: Args, pointer: string): Args => {
  const parts = segmentsOf(pointer);
  const drop = (node: Json, i: number): Json | undefined => {
    if (!isObj(node)) return node;
    const k = parts[i]!;
    const out: JsonObject = { ...node };
    if (i === parts.length - 1) delete out[k];
    else if (out[k] !== undefined) {
      const inner = drop(out[k]!, i + 1);
      if (inner === undefined || (isObj(inner) && Object.keys(inner).length === 0)) delete out[k];
      else out[k] = inner;
    }
    return out;
  };
  return (drop(args as JsonObject, 0) ?? {}) as Args;
};

// -- what a hole accepts -----------------------------------------------------------------------

/** A hole before its `accepts` is known, with the schema as the spec wrote it (refs intact). */
export interface HoleDraft {
  readonly name: string;
  readonly in: Hole['in'];
  readonly ref: string | null;
  readonly hidden: boolean;
  /** The property or parameter schema before inlining: `$ref`s tell what it is. */
  readonly raw: Json;
  /** The operation's path, for `id` under a REST collection. */
  readonly path: string;
}

const POSITION_FIELDS = new Set(['date', 'hour', 'parametric', 'coordinates']);
const POSITION_REFS = new Set(['Date', 'Hour', 'Parametric', 'Coordinates', 'At', 'Position', 'Moment']);
const WORLD_REFS = new Set(['WorldName', 'GlobalName']);
const COLLECTION_IDS: readonly (readonly [string, NounKind])[] = [['/v1/keys', 'key'], ['/v1/grants', 'grant'], ['/v1/webhooks', 'webhook']];

/** `legs[].hand` → `hand`. */
export const leafOf = (name: string): string => name.slice(name.lastIndexOf('.') + 1).replace(/\[\]$/, '');

const refOfRaw = (raw: Json): string | null => {
  if (!isObj(raw)) return null;
  if (typeof raw.$ref === 'string') return raw.$ref.slice(raw.$ref.lastIndexOf('/') + 1);
  if (isObj(raw.items) && typeof raw.items.$ref === 'string') return raw.items.$ref.slice(raw.items.$ref.lastIndexOf('/') + 1);
  return null;
};

/** The kinds of noun a hole takes. Always includes `value`: anything can be typed. */
export const acceptsOf = (h: HoleDraft): NounKind[] => {
  const out = new Set<NounKind>();
  if (!h.hidden) {
    const leaf = leafOf(h.name);
    const own = refOfRaw(h.raw);
    const refs = new Set([h.ref, own].filter((r): r is string => r !== null));
    if ((h.in === 'path' && h.name === 'w') || refs.has('World')) out.add('world');
    if ((h.in === 'path' && h.name === 'r') || refs.has('RelayName')) out.add('relay');
    if ((h.in === 'path' && h.name === 'token') || refs.has('Token')) out.add('ask');
    if (h.in === 'path' && h.name === 'id') {
      for (const [prefix, kind] of COLLECTION_IDS) if (h.path.startsWith(prefix)) out.add(kind);
    }
    if (POSITION_FIELDS.has(leaf) && (h.in === 'query' || h.in === 'body' || [...refs].some((r) => POSITION_REFS.has(r)))) {
      // A run and a tick both sit at a position, so either can say where.
      out.add('position');
      out.add('moment');
      out.add('selection');
    }
    if (refs.has('SlotsInput') || (h.in === 'body' && leaf === 'slots')) { out.add('selection'); out.add('moment'); }
    if (refs.has('OccupierRef') || (h.in === 'query' && leaf === 'who')) out.add('occupier');
    // A World named where the body wants another World: `hand`, a leg's hand, a delegate's `to`, scope lists.
    const worldish = (own !== null && WORLD_REFS.has(own)) || leaf === 'hand' || leaf === 'world' || (leaf === 'to' && refs.has('RelayAct'));
    if (worldish && leaf !== 'name' && h.in === 'body') out.add('world');
    if (leaf === 'start' || refs.has('Moment')) out.add('moment');
    if (h.in === 'body' && leaf === 'leg') out.add('leg');
    if (h.in === 'query' && leaf === 'since') out.add('commit');
    if (leaf === 'plan') out.add('plan');
    if (leaf === 'preset') out.add('preset');
    if (leaf === 'workspace') out.add('workspace');
  }
  out.add('value');
  return [...out];
};
